import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthSearchOpsTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import { SearchWorkHomeDto } from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-work-home',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    FormsModule,
    SelectModule,
    TableModule,
    ButtonModule,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.searchops.workHome.title')"
      [description]="t('growth.searchops.workHome.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.workHome.help.title'"
        [descriptionKey]="'growth.searchops.workHome.help.description'"
        [bulletKeys]="[
          'growth.searchops.workHome.help.bullets.property',
          'growth.searchops.workHome.help.bullets.positions',
          'growth.searchops.workHome.help.bullets.inbox'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load(true)"></button>
        <a pButton [routerLink]="['/growth/search-ops/properties']" [label]="t('growth.searchops.workHome.actions.manageProperties')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/backlog']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.backlog')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/coworkers']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.coworkers')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/keywords']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.keywords')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/urls']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.urls')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/issues']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.issues')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/redirects']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.redirects')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/apply-queue']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.applyQueue')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/offpage']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.offpage')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/local']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.local')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/sem']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.sem')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/scorecard']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.scorecard')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/reports']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.reports')"></a>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error()" />
      } @else if (!home() || home()!.properties.length === 0) {
        <ain-state type="empty" [title]="t('growth.searchops.workHome.empty.title')" [message]="t('growth.searchops.workHome.empty.message')" />
      } @else {
        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.workHome.sections.property') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.workHome.propertyHelp.title'"
              [descriptionKey]="'growth.searchops.workHome.propertyHelp.description'" />
          </div>
          <ain-form-field [label]="t('growth.searchops.fields.property')">
            <p-select
              [options]="propertyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedPropertyId"
              (ngModelChange)="onPropertyChange($event)"
              appendTo="body" />
          </ain-form-field>
          @if (home()?.selectedProperty; as property) {
            <p class="summary">
              {{ property.primaryDomain }} · {{ t('growth.searchops.siteKind.' + property.siteKind) }} ·
              {{ t('growth.searchops.status.' + property.status) }}
            </p>
            <a [routerLink]="['/growth/search-ops/properties', property.id]">{{ t('growth.searchops.workHome.actions.openProperty') }}</a>
          }
        </section>

        @if (home()?.executiveSnapshot; as snapshot) {
          <section class="card">
            <div class="section-title">
              <h3>{{ t('growth.searchops.workHome.sections.snapshot') }}</h3>
              <ain-info-hint
                [titleKey]="'growth.searchops.workHome.snapshotHelp.title'"
                [descriptionKey]="'growth.searchops.workHome.snapshotHelp.description'" />
            </div>
            <p-table [value]="snapshotRows()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.searchops.fields.metric') }}</th>
                  <th>{{ t('growth.searchops.fields.current') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.label }}</td>
                  <td>{{ row.value }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>
        }

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.workHome.sections.myPositions') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.workHome.positionsHelp.title'"
              [descriptionKey]="'growth.searchops.workHome.positionsHelp.description'" />
          </div>
          @if ((home()?.myPositions?.length ?? 0) === 0) {
            <ain-state type="empty" [title]="t('growth.searchops.workHome.positions.empty')" />
          } @else {
            <p-table [value]="home()!.myPositions">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.searchops.fields.position') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>
                    <a [routerLink]="['/growth/search-ops/backlog']" [queryParams]="{ propertyId: selectedPropertyId, position: row.positionCode }">
                      {{ t('growth.searchops.positions.' + row.positionCode) }}
                    </a>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.workHome.sections.inbox') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.workHome.inboxHelp.title'"
              [descriptionKey]="'growth.searchops.workHome.inboxHelp.description'" />
          </div>
          @if ((home()?.coworkerInbox?.length ?? 0) === 0 && (home()?.coworkerReadyTasks?.length ?? 0) === 0) {
            <ain-state type="empty" [title]="t('growth.searchops.workHome.inbox.empty')" />
          } @else {
            @if ((home()?.coworkerInbox?.length ?? 0) > 0) {
              <p-table [value]="home()!.coworkerInbox">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('growth.searchops.fields.kind') }}</th>
                    <th>{{ t('growth.searchops.fields.status') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ t('growth.searchops.recommendationKind.' + row.kind) }}</td>
                    <td>{{ t('growth.searchops.recommendationStatus.' + row.status) }}</td>
                    <td class="actions">
                      <button pButton type="button" size="small" [label]="t('growth.searchops.workHome.actions.accept')" (click)="accept(row.id)"></button>
                      <button pButton type="button" size="small" severity="secondary" [label]="t('growth.searchops.workHome.actions.reject')" (click)="reject(row.id)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
            @if ((home()?.coworkerReadyTasks?.length ?? 0) > 0) {
              <p-table [value]="home()!.coworkerReadyTasks">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('growth.searchops.fields.title') }}</th>
                    <th>{{ t('growth.searchops.fields.position') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>
                      <a [routerLink]="['/growth/search-ops/backlog']" [queryParams]="{ propertyId: selectedPropertyId, position: row.positionCode }">{{ row.title }}</a>
                    </td>
                    <td>{{ t('growth.searchops.positions.' + row.positionCode) }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.workHome.sections.discovery') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.workHome.discoveryHelp.title'"
              [descriptionKey]="'growth.searchops.workHome.discoveryHelp.description'" />
          </div>
          <a pButton [routerLink]="home()?.seoProfilesPath || '/growth/seo/profiles'" [label]="t('growth.searchops.workHome.actions.openSeoProfiles')"></a>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 12px; }
    .card { background: var(--ain-color-surface, #fff); padding: 16px; border-radius: 12px; margin-bottom: 12px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
    .section-title h3 { margin: 0; font-size: 1rem; }
    .summary { color: #64748b; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
  `]
})
export class GrowthSearchOpsWorkHomeComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly home = signal<SearchWorkHomeDto | null>(null);
  readonly loading = signal(false);
  readonly error = signal('');
  selectedPropertyId: string | null = null;
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthSearchOpsChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(false));
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.selectedPropertyId = params.get('propertyId');
      this.load(true);
    });
    void this.setupLiveSubscription();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  propertyOptions(): Array<{ label: string; value: string }> {
    return (this.home()?.properties ?? []).map(item => ({
      value: item.id,
      label: `${item.name} (${item.primaryDomain})`
    }));
  }

  propertyQuery(): Record<string, string> {
    return this.selectedPropertyId ? { propertyId: this.selectedPropertyId } : {};
  }

  snapshotRows(): Array<{ label: string; value: string }> {
    const snapshot = this.home()?.executiveSnapshot;
    if (!snapshot) {
      return [];
    }
    return [
      { label: this.t('growth.searchops.scorecard.metric.clicks'), value: snapshot.clicks == null ? '—' : `${snapshot.clicks}` },
      { label: this.t('growth.searchops.scorecard.metric.coreRank'), value: snapshot.coreRank == null ? '—' : `${snapshot.coreRank}` },
      { label: this.t('growth.searchops.scorecard.metric.openCriticalIssues'), value: snapshot.openCriticalIssues == null ? '—' : `${snapshot.openCriticalIssues}` }
    ];
  }

  accept(id: string): void {
    this.api.acceptRecommendation(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.load(false),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  reject(id: string): void {
    this.api.rejectRecommendation(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.load(false),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  onPropertyChange(propertyId: string): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { propertyId },
      queryParamsHandling: 'merge'
    });
  }

  load(showSpinner: boolean): void {
    if (showSpinner) {
      this.loading.set(true);
    }
    this.error.set('');
    this.api.getWorkHome(this.selectedPropertyId).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: home => {
        this.home.set(home);
        if (!this.selectedPropertyId && home.selectedProperty) {
          this.selectedPropertyId = home.selectedProperty.id;
        }
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthSearchOpsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
